// React hook for Trailing Stop-Loss integration
import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  Position,
  TrailingStopLossConfig,
  TrailingStopLossEvent,
  TrailingStopLossState
} from '../types/mexc';
import { TrailingStopLossEngine } from '../services/trailingStopLossEngine';
import { TrailingStopLossPersistenceService } from '../services/trailingStopLossPersistence';
import { usePricePolling } from './usePricePolling';
import { useTicker } from './useMexcApi';
import { tslLogger } from '../utils/logger';

interface UseTrailingStopLossReturn {
  tslState: TrailingStopLossState;
  isEngineRunning: boolean;
  activeConfigs: TrailingStopLossConfig[];
  events: TrailingStopLossEvent[];
  startEngine: () => void;
  stopEngine: () => void;
  enableTSL: (positionId: number, config: Omit<TrailingStopLossConfig, 'positionId' | 'createdAt' | 'updatedAt'>) => void;
  disableTSL: (positionId: number) => void;
  getTSLConfig: (positionId: number) => TrailingStopLossConfig | null;
  clearEvents: () => void;
  engineStatus: {
    isRunning: boolean;
    activeConfigs: number;
    lastProcessTime: number | null;
  };
}

export function useTrailingStopLoss(): UseTrailingStopLossReturn {
  const [tslState, setTslState] = useState<TrailingStopLossState>(() =>
    TrailingStopLossPersistenceService.getTSLState()
  );
  const [isEngineRunning, setIsEngineRunning] = useState(false);
  const [events, setEvents] = useState<TrailingStopLossEvent[]>([]);
  const [engineStatus, setEngineStatus] = useState({
    isRunning: false,
    activeConfigs: 0,
    lastProcessTime: null as number | null,
  });

  const engineRef = useRef<TrailingStopLossEngine | undefined>(undefined);
  const priceData = usePricePolling();

  // Initialize TSL engine
  useEffect(() => {
    engineRef.current = TrailingStopLossEngine.getInstance();

    // Set up event listener
    const handleTSLEvent = (event: TrailingStopLossEvent) => {
      setEvents(prev => [...prev.slice(-49), event]); // Keep last 50 events

      // Update state when configs change
      if (event.type === 'enabled' || event.type === 'disabled' || event.type === 'updated') {
        setTslState(TrailingStopLossPersistenceService.getTSLState());
      }
    };

    engineRef.current.addEventListener(handleTSLEvent);

    // Update engine status
    const updateStatus = () => {
      if (engineRef.current) {
        const status = engineRef.current.getStatus();
        setEngineStatus(status);
        setIsEngineRunning(status.isRunning);
      }
    };

    updateStatus();
    const statusInterval = setInterval(updateStatus, 10000); // Update every 10 seconds

    return () => {
      if (engineRef.current) {
        engineRef.current.removeEventListener(handleTSLEvent);
      }
      clearInterval(statusInterval);
    };
  }, []);

  // Update TSL engine with price data from polling
  useEffect(() => {
    if (!engineRef.current || Object.keys(priceData.tickers).length === 0) return;

    // Get TSL configurations to determine which symbols need logging
    const tslState = TrailingStopLossPersistenceService.getTSLState();
    const tslSymbols = new Set(Object.values(tslState.configs).map(config => config.symbol));

    // Priority symbols that should always be logged (for debugging)
    const prioritySymbols = new Set(['SOL_USDT', 'ENS_USDT']);

    // Update prices from polled ticker data with intelligent logging
    Object.entries(priceData.tickers).forEach(([symbol, ticker]) => {
      if (ticker.lastPrice && ticker.lastPrice > 0) {
        // Always update the TSL engine
        engineRef.current!.updatePrice(symbol, ticker.lastPrice);

        // Determine logging priority
        const isPriority = prioritySymbols.has(symbol);
        const hasTSL = tslSymbols.has(symbol);

        if (isPriority || hasTSL) {
          const priority = hasTSL ? 'high' : (isPriority ? 'medium' : 'low');
          tslLogger.priceUpdate(symbol, ticker.lastPrice, priority);
        }
      }
    });
  }, [priceData.tickers, priceData.lastPollTime]);

  const startEngine = useCallback(() => {
    if (engineRef.current && !isEngineRunning) {
      engineRef.current.start();
      setIsEngineRunning(true);
    }
  }, [isEngineRunning]);

  const stopEngine = useCallback(() => {
    if (engineRef.current && isEngineRunning) {
      engineRef.current.stop();
      setIsEngineRunning(false);
    }
  }, [isEngineRunning]);

  // Auto-start engine when global TSL is enabled
  // Engine must be running for Auto-TSL to detect new positions and apply configs
  useEffect(() => {
    if (tslState.globalEnabled && !isEngineRunning) {
      startEngine();
    }
  }, [tslState.globalEnabled, isEngineRunning, startEngine]);

  const enableTSL = useCallback((
    positionId: number,
    configData: Omit<TrailingStopLossConfig, 'positionId' | 'createdAt' | 'updatedAt'>
  ) => {
    const config: TrailingStopLossConfig = {
      ...configData,
      positionId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    TrailingStopLossPersistenceService.saveTSLConfig(config);
    setTslState(TrailingStopLossPersistenceService.getTSLState());

    // Start engine if not running
    if (!isEngineRunning) {
      startEngine();
    }
  }, [isEngineRunning, startEngine]);

  const disableTSL = useCallback((positionId: number) => {
    const config = TrailingStopLossPersistenceService.getTSLConfig(positionId);
    if (config) {
      const updatedConfig: TrailingStopLossConfig = {
        ...config,
        enabled: false,
        isActive: false,
        updatedAt: Date.now(),
      };

      TrailingStopLossPersistenceService.saveTSLConfig(updatedConfig);
      setTslState(TrailingStopLossPersistenceService.getTSLState());

      // Deactivate in engine
      if (engineRef.current) {
        engineRef.current.deactivateTSL(positionId, 'Disabled by user');
      }
    }
  }, []);

  const getTSLConfig = useCallback((positionId: number): TrailingStopLossConfig | null => {
    return TrailingStopLossPersistenceService.getTSLConfig(positionId);
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const activeConfigs = Object.values(tslState.configs).filter(
    config => config.enabled && config.isActive
  );

  return {
    tslState,
    isEngineRunning,
    activeConfigs,
    events,
    startEngine,
    stopEngine,
    enableTSL,
    disableTSL,
    getTSLConfig,
    clearEvents,
    engineStatus,
  };
}

// Hook for TSL configuration of a specific position
export function usePositionTSL(position: Position) {
  const { getTSLConfig, enableTSL, disableTSL } = useTrailingStopLoss();
  const [config, setConfig] = useState<TrailingStopLossConfig | null>(null);

  // Get ticker data for real-time price
  const ticker = useTicker(position.symbol);

  useEffect(() => {
    const tslConfig = getTSLConfig(position.positionId);
    setConfig(tslConfig);
  }, [position.positionId, getTSLConfig]);

  const updateConfig = useCallback((
    newConfigData: Omit<TrailingStopLossConfig, 'positionId' | 'createdAt' | 'updatedAt'>
  ) => {
    enableTSL(position.positionId, newConfigData);
    setConfig(getTSLConfig(position.positionId));
  }, [position.positionId, enableTSL, getTSLConfig]);

  const disable = useCallback(() => {
    disableTSL(position.positionId);
    setConfig(null);
  }, [position.positionId, disableTSL]);

  return {
    config,
    currentPrice: (ticker.data && !Array.isArray(ticker.data) ? ticker.data.lastPrice : undefined) || position.holdAvgPrice,
    isLoading: ticker.isLoading,
    updateConfig,
    disable,
  };
}

// Hook for global TSL management
export function useGlobalTSL() {
  const [globalEnabled, setGlobalEnabled] = useState(() =>
    TrailingStopLossPersistenceService.getGlobalEnabled()
  );
  const [defaultSettings, setDefaultSettings] = useState(() =>
    TrailingStopLossPersistenceService.getGlobalSettings()
  );

  const updateGlobalEnabled = useCallback((enabled: boolean) => {
    TrailingStopLossPersistenceService.setGlobalEnabled(enabled);
    setGlobalEnabled(enabled);
  }, []);

  const updateDefaultSettings = useCallback((
    settings: Partial<TrailingStopLossState['defaultSettings']>
  ) => {
    TrailingStopLossPersistenceService.saveGlobalSettings(settings);
    setDefaultSettings(TrailingStopLossPersistenceService.getGlobalSettings());
  }, []);

  const getStats = useCallback(() => {
    return TrailingStopLossPersistenceService.getStats();
  }, []);

  return {
    globalEnabled,
    defaultSettings,
    updateGlobalEnabled,
    updateDefaultSettings,
    getStats,
  };
}
